import { Badge } from '../ui/badge';
import { Progress } from '../../components/ui/progress';
import { TableCell, TableRow } from '../ui/table';
import { formatCurrency, formatPercent } from '../../lib/utils';
import { ExternalLink } from 'lucide-react';

export interface ProjectData {
  id: string;
  projectNumber: string;
  name: string;
  brandCode: 'EQ' | 'SR';
  brandName: string;
  brandColor?: string;
  clientName: string;
  status: 'ADJUDICAT' | 'OFICINA_TECNICA' | 'COMPRES' | 'FABRICACIO_TALLER' | 'PREPARAT_MUNTATGE' | 'EN_MUNTATGE' | 'TANCAMENT' | 'CANCELLED';
  category: string;
  estimatedBudget: number;
  actualCost: number;
  estimatedMargin: number;
  actualMargin: number;
  laborCost: number;
  materialCost: number;
  startDate: string;
  endDate?: string;
}

const statusConfig: Record<ProjectData['status'], { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  ADJUDICAT: { label: 'Adjudicat', variant: 'secondary' },
  OFICINA_TECNICA: { label: 'Oficina Tècnica', variant: 'outline' },
  COMPRES: { label: 'Compres', variant: 'outline' },
  FABRICACIO_TALLER: { label: 'Fabricació Taller', variant: 'default' },
  PREPARAT_MUNTATGE: { label: 'Preparat Muntatge', variant: 'default' },
  EN_MUNTATGE: { label: 'En Muntatge', variant: 'destructive' },
  TANCAMENT: { label: 'Tancament', variant: 'default' },
  CANCELLED: { label: 'Cancel·lat', variant: 'outline' },
};

interface ProjectRowProps {
  project: ProjectData;
  onClick?: (project: ProjectData) => void;
}

export function ProjectRow({ project, onClick }: ProjectRowProps) {
  const budgetConsumed = project.estimatedBudget > 0 
    ? (project.actualCost / project.estimatedBudget) * 100 
    : 0;
  
  const marginDeviation = project.actualMargin - project.estimatedMargin;
  const isOverBudget = budgetConsumed > 100;
  const isNearBudget = budgetConsumed >= 90 && budgetConsumed <= 100;

  const statusInfo = statusConfig[project.status] || statusConfig.ADJUDICAT;

  return (
    <TableRow 
      className="cursor-pointer hover:bg-slate-50"
      onClick={() => onClick?.(project)}
    >
      {/* Project Number + Brand Badge */}
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <Badge
            variant="default"
            className={!project.brandColor ? (project.brandCode === 'EQ' ? 'bg-blue-600' : 'bg-purple-600') : ''}
            style={project.brandColor ? { backgroundColor: project.brandColor } : undefined}
          >
            {project.brandCode}
          </Badge>
          <span>{project.projectNumber}</span>
        </div>
      </TableCell>

      {/* Project Name */}
      <TableCell>
        <div className="max-w-[200px]">
          <p className="font-medium truncate">{project.name}</p>
          <p className="text-xs text-muted-foreground truncate">{project.clientName}</p>
        </div>
      </TableCell>

      {/* Budget Progress */}
      <TableCell className="min-w-[150px]">
        <div className="space-y-1">
          <Progress 
            value={budgetConsumed} 
            max={100} 
            showLabel 
            variant={isOverBudget ? 'danger' : isNearBudget ? 'warning' : 'success'}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{formatCurrency(project.actualCost)}</span>
            <span>/ {formatCurrency(project.estimatedBudget)}</span>
          </div>
        </div>
      </TableCell>

      {/* Costs Breakdown */}
      <TableCell>
        <div className="text-xs space-y-1">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">M.O.:</span>
            <span>{formatCurrency(project.laborCost)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Mat.:</span>
            <span>{formatCurrency(project.materialCost)}</span>
          </div>
        </div>
      </TableCell>

      {/* Margin */}
      <TableCell>
        <div className="text-right">
          <p className={`font-semibold ${marginDeviation < 0 ? 'text-red-600' : 'text-green-600'}`}>
            {formatPercent(project.actualMargin)}
          </p>
          <p className="text-xs text-muted-foreground">
            Est: {formatPercent(project.estimatedMargin)}
          </p>
          {marginDeviation !== 0 && (
            <p className={`text-xs ${marginDeviation < 0 ? 'text-red-500' : 'text-green-500'}`}>
              {marginDeviation > 0 ? '+' : ''}{formatPercent(marginDeviation)}
            </p>
          )}
        </div>
      </TableCell>

      {/* Status */}
      <TableCell>
        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
      </TableCell>

      {/* Actions */}
      <TableCell>
        <button
          className="p-1 hover:bg-slate-100 rounded"
          onClick={(e) => {
            e.stopPropagation();
            // Navigate to project detail
          }}
        >
          <ExternalLink className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>
    </TableRow>
  );
}
